import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserRound, Building2, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";

/**
 * ALTIORA V3 — Dossier Creation (conversational, single-idea-per-screen)
 * Doctrine : /app/memory/ALTIORA_V3_ARCHITECTURE.md
 *
 * Query params (optional):
 *   elia=<slug>              ELIA module that spawned this dossier
 *   subtype=<key>            e.g. enfant, conjoint, entreprise, patrimoine_famille
 *   parent_id=<dossier_id>   parent dossier (nested creation)
 *   client_type=individual|legal_entity  Pre-selects the type
 *
 * Flow :
 *   STEP 1 · CHOICE (skipped if client_type is provided or subtype implies it)
 *   STEP 2 · IDENTITY    → Prénom/Nom  OR  Raison sociale + NEQ
 *   STEP 3 · CREATING    → Création + activation IMES par défaut
 *   STEP 4 · ARRIVED     → Redirect vers vue dossier
 */

const STEPS = { CHOICE: 0, IDENTITY: 1, CREATING: 2, ARRIVED: 3 };

const SUBTYPE_LABELS = {
  enfant: { title: "Un dossier pour un enfant", subtitle: "REEE, garde, éducation, transmission." },
  conjoint: { title: "Un dossier pour votre conjoint(e)", subtitle: "Lié au vôtre pour les décisions communes." },
  patrimoine_famille: { title: "Un dossier de patrimoine familial", subtitle: "Immobilier, épargne, biens partagés." },
  entreprise: { title: "Un dossier d'entreprise", subtitle: "PME, société par actions, indépendant incorporé." },
  actionnaire: { title: "Un dossier d'actionnaire", subtitle: "Pour un co-actionnaire ou associé." },
  beneficiary: { title: "Un dossier bénéficiaire", subtitle: "Suivi institutionnel accompagné." },
  self: { title: "Votre dossier principal", subtitle: "La base économique unifiée qui vous suit dans le temps." },
};

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

export default function DossierNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eliaModule = searchParams.get("elia") || null;
  const subtype = searchParams.get("subtype") || null;
  const parentId = searchParams.get("parent_id") || null;
  const preClientType = searchParams.get("client_type");
  const forClient = searchParams.get("for_client") === "1";  // Pro shadow-client mode

  const [step, setStep] = useState(preClientType ? STEPS.IDENTITY : STEPS.CHOICE);
  // In shadow-client mode we force "individual" — Pros open dossiers FOR
  // a natural person by default. Corporate clients follow the standard path.
  const [type, setType] = useState(preClientType || (forClient ? "individual" : null));
  const [busy, setBusy] = useState(false);

  // Individual fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("CA");  // ISO alpha-2 — drives comparator scope

  // Shadow-client fields (only used when forClient=true)
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientBirthDate, setClientBirthDate] = useState("");
  const [clientNotes, setClientNotes] = useState("");

  // Legal entity fields
  const [legalName, setLegalName] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [legalForm, setLegalForm] = useState("");
  const [sector, setSector] = useState("");

  useEffect(() => {
    if (forClient) {
      setType("individual");
      setStep(STEPS.IDENTITY);
    } else if (preClientType && (preClientType === "individual" || preClientType === "legal_entity")) {
      setType(preClientType);
      setStep(STEPS.IDENTITY);
    }
  }, [preClientType, forClient]);

  const canSubmit =
    type === "individual"
      ? firstName.trim() && lastName.trim()
      : legalName.trim();

  const subtypeMeta = subtype ? SUBTYPE_LABELS[subtype] : null;

  const createDossier = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setStep(STEPS.CREATING);
    try {
      const name =
        type === "individual"
          ? `${firstName.trim()} ${lastName.trim()}`
          : legalName.trim();

      const payload = {
        type,
        name,
        jurisdiction: "CA-QC",
        subtype: subtype || undefined,
        elia_module: eliaModule || undefined,
        parent_dossier_id: parentId || undefined,
        individual:
          type === "individual"
            ? {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                residence_city: city.trim() || null,
                city: city.trim() || null,
                country: country,
                language: "fr",
              }
            : null,
        legal_entity:
          type === "legal_entity"
            ? {
                legal_name: legalName.trim(),
                business_number: businessNumber.trim() || null,
                legal_form: legalForm.trim() || null,
                sector: sector.trim() || null,
                country: country,
              }
            : null,
        // Shadow-client mode: the Pro captures the client's identity without
        // requiring a user account. The backend enforces the role check.
        client_metadata: forClient
          ? {
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              email: clientEmail.trim() || null,
              phone: clientPhone.trim() || null,
              city: city.trim() || null,
              birth_date: clientBirthDate || null,
              language: "fr",
              notes: clientNotes.trim() || null,
            }
          : undefined,
      };

      const { data } = await api.post("/dossiers/", payload);
      setStep(STEPS.ARRIVED);
      // Where to go next after creation:
      //   • ELIA-driven → adaptive questionnaire
      //   • Pro shadow-client → questionnaire directly (start the parcours)
      //   • Otherwise → dossier detail (overview + module activation)
      const next = eliaModule || forClient
        ? `/app/dossiers/${data.dossier_id}/questionnaire`
        : `/app/dossiers/${data.dossier_id}`;
      setTimeout(() => { navigate(next); }, 1800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Création impossible", {
        duration: 8000,
      });
      setStep(STEPS.IDENTITY);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937] relative overflow-hidden">
      {/* Ambient warmth */}
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-[#DCC29A]/15 blur-3xl" />
        <div className="absolute bottom-[-30%] right-[-10%] w-[700px] h-[700px] rounded-full bg-[#0B2545]/10 blur-3xl" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 py-5">
        <div className="flex items-center gap-3">
          <AltioraTreeLogo size={40} />
          <div className="leading-tight">
            <div className="font-display text-lg tracking-tight text-[#0B2545]">ALTIORA</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-[#7A5B14]">
              {forClient ? "Nouveau dossier client" : "Nouveau dossier"}
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="text-xs font-mono uppercase tracking-[0.2em] text-[#5B5B62] hover:text-[#0B2545] transition-colors"
          data-testid="dossier-new-back"
        >
          Annuler
        </button>
      </header>

      <main
        className="relative z-10 max-w-3xl mx-auto px-6 md:px-8 pt-6 md:pt-16 pb-24"
        data-testid="dossier-new-page"
      >
        <AnimatePresence mode="wait">
          {/* -------- STEP 1 · CHOICE -------- */}
          {step === STEPS.CHOICE && (
            <motion.div key="choice" {...fade}>
              <div className="text-xs font-mono uppercase tracking-[0.32em] text-[#7A5B14]">
                Étape 1 sur 2
              </div>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Pour qui ouvrons-nous<br />
                <span className="text-[#0B2545]">ce dossier ?</span>
              </h1>
              <p className="mt-6 text-[15px] md:text-base leading-relaxed text-[#5B5B62] max-w-xl">
                Un dossier ALTIORA, c&apos;est votre base économique unifiée — elle vous accompagnera
                à travers les modules dont vous avez besoin, aujourd&apos;hui et demain.
              </p>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                <ChoiceCard
                  testId="dossier-new-choice-individual"
                  icon={<UserRound className="h-6 w-6" strokeWidth={1.5} />}
                  title="Une personne"
                  subtitle="Particulier, famille, projet personnel."
                  onClick={() => {
                    setType("individual");
                    setStep(STEPS.IDENTITY);
                  }}
                />
                <ChoiceCard
                  testId="dossier-new-choice-legal-entity"
                  icon={<Building2 className="h-6 w-6" strokeWidth={1.5} />}
                  title="Une organisation"
                  subtitle="Entreprise, OBNL, institution, cabinet."
                  onClick={() => {
                    setType("legal_entity");
                    setStep(STEPS.IDENTITY);
                  }}
                />
              </div>

              <div className="mt-10 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.24em] text-[#8B8B8F]">
                <Sparkles className="h-3 w-3" strokeWidth={2} />
                Vous pourrez toujours ouvrir un second dossier plus tard.
              </div>
            </motion.div>
          )}

          {/* -------- STEP 2 · IDENTITY -------- */}
          {step === STEPS.IDENTITY && type === "individual" && (
            <motion.div key="ident-ind" {...fade}>
              <div className="text-xs font-mono uppercase tracking-[0.32em] text-[#7A5B14]">
                {forClient
                  ? "Fiche client · vous restez seul propriétaire"
                  : (subtypeMeta ? `ALTIORA · ${subtypeMeta.title}` : "Étape 2 sur 2 · Personne physique")}
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
                {forClient
                  ? <>Ouvrons le dossier<br />de votre client.</>
                  : (subtypeMeta && subtype !== "self"
                    ? <>Ouvrons ce dossier<br />avec un prénom et un nom.</>
                    : <>Commençons par un prénom<br />et un nom.</>)}
              </h2>
              <p className="mt-4 text-[#5B5B62] leading-relaxed max-w-lg">
                {forClient
                  ? "Votre client n'a pas besoin d'un compte ALTIORA. Vous documenterez son parcours économico-social — puis nous générerons le diagnostic et le rapport."
                  : (subtypeMeta?.subtitle || "Nous n'avons besoin que du strict minimum pour ouvrir votre dossier.")}
                {" "}
                {!forClient && "Le reste viendra à votre rythme, question par question."}
              </p>

              <div className="mt-10 space-y-5 max-w-md">
                <div className="grid grid-cols-2 gap-3">
                  <TextInput label="Prénom" testId="dossier-new-firstname" value={firstName} setValue={setFirstName} autoFocus />
                  <TextInput label="Nom" testId="dossier-new-lastname" value={lastName} setValue={setLastName} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <TextInput label="Ville de résidence (facultatif)" testId="dossier-new-city" value={city} setValue={setCity} />
                  <CountrySelect country={country} setCountry={setCountry} testId="dossier-new-country" />
                </div>
                {forClient && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <TextInput label="Courriel du client (facultatif)" testId="dossier-new-client-email" value={clientEmail} setValue={setClientEmail} />
                      <TextInput label="Téléphone (facultatif)" testId="dossier-new-client-phone" value={clientPhone} setValue={setClientPhone} />
                    </div>
                    <label className="block">
                      <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">Date de naissance (facultatif)</div>
                      <input
                        data-testid="dossier-new-client-birth"
                        type="date"
                        value={clientBirthDate}
                        onChange={(e) => setClientBirthDate(e.target.value)}
                        className="w-full rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
                      />
                    </label>
                    <label className="block">
                      <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">Notes rapides (facultatif)</div>
                      <textarea
                        data-testid="dossier-new-client-notes"
                        rows={3}
                        value={clientNotes}
                        onChange={(e) => setClientNotes(e.target.value)}
                        placeholder="Ex : arrivé du Maroc en 2024, ingénieur en TI, cherche à optimiser sa fiscalité et acheter une propriété."
                        className="w-full rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
                      />
                    </label>
                  </>
                )}
                <ContinueButton
                  disabled={!canSubmit || busy}
                  onClick={createDossier}
                  testId="dossier-new-submit"
                  label={forClient ? "Ouvrir le dossier client" : "Ouvrir mon dossier"}
                />
              </div>
            </motion.div>
          )}

          {step === STEPS.IDENTITY && type === "legal_entity" && (
            <motion.div key="ident-le" {...fade}>
              <div className="text-xs font-mono uppercase tracking-[0.32em] text-[#7A5B14]">
                {subtypeMeta ? `ALTIORA · ${subtypeMeta.title}` : "Étape 2 sur 2 · Personne morale"}
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
                Une organisation<br />a-t-elle une raison sociale ?
              </h2>
              <p className="mt-4 text-[#5B5B62] leading-relaxed max-w-lg">
                {subtypeMeta?.subtitle || "Nous partons de son nom légal."} Vous pourrez enrichir les
                informations réglementaires (NEQ, dirigeants, actionnaires, états financiers) plus tard.
              </p>

              <div className="mt-10 space-y-5 max-w-md">
                <TextInput label="Raison sociale" testId="dossier-new-legalname" value={legalName} setValue={setLegalName} autoFocus />
                <div className="grid grid-cols-2 gap-3">
                  <TextInput label="NEQ / n° d'immatriculation (facultatif)" testId="dossier-new-neq" value={businessNumber} setValue={setBusinessNumber} />
                  <TextInput label="Forme juridique (Inc., SARL, OBNL...)" testId="dossier-new-legalform" value={legalForm} setValue={setLegalForm} />
                </div>
                <TextInput label="Secteur d'activité (facultatif)" testId="dossier-new-sector" value={sector} setValue={setSector} />
                <CountrySelect country={country} setCountry={setCountry} testId="dossier-new-country-entity" />
                <ContinueButton disabled={!canSubmit || busy} onClick={createDossier} testId="dossier-new-submit" />
              </div>
            </motion.div>
          )}

          {/* -------- STEP 3 · CREATING -------- */}
          {step === STEPS.CREATING && (
            <motion.div key="creating" {...fade} className="flex flex-col items-center text-center py-24">
              <Loader2 className="h-6 w-6 text-[#0B2545] animate-spin" strokeWidth={1.5} />
              <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.32em] text-[#5B5B62]">
                Création de votre dossier…
              </div>
              <div className="mt-4 text-[#374151] max-w-sm leading-relaxed">
                Nous initialisons votre base économique unifiée et activons votre premier module :
                <span className="text-[#0B2545] font-medium"> Trajectoire économique personnelle</span>.
              </div>
            </motion.div>
          )}

          {/* -------- STEP 4 · ARRIVED -------- */}
          {step === STEPS.ARRIVED && (
            <motion.div key="arrived" {...fade} className="pt-12 md:pt-24">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="flex justify-center"
              >
                <AltioraTreeLogo size={72} />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.7 }}
                className="mt-8 text-center font-display text-3xl md:text-4xl tracking-tight"
              >
                Votre dossier est ouvert.
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="mt-4 text-center text-[#5B5B62] leading-relaxed"
              >
                Nous vous y emmenons maintenant.
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

/* ================== Sub-components ================== */

function ChoiceCard({ testId, icon, title, subtitle, onClick }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="group text-left rounded-xl border border-[#E4E1D8] bg-white hover:border-[#0B2545] hover:shadow-lg transition-all p-6"
    >
      <div className="h-12 w-12 rounded-lg bg-[#0B2545]/5 group-hover:bg-[#0B2545] text-[#0B2545] group-hover:text-white transition-colors flex items-center justify-center">
        {icon}
      </div>
      <div className="mt-5 font-display text-xl text-[#1F2937] tracking-tight">{title}</div>
      <div className="mt-2 text-sm text-[#5B5B62] leading-relaxed">{subtitle}</div>
      <div className="mt-6 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.24em] text-[#8B8B8F] group-hover:text-[#0B2545] transition-colors">
        Ouvrir un dossier
        <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
      </div>
    </button>
  );
}

function TextInput({ label, value, setValue, testId, autoFocus }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">{label}</div>
      <input
        data-testid={testId}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus={autoFocus}
        className="w-full rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
      />
    </label>
  );
}

/* Country dropdown — drives which institution catalog subset the comparator uses
 * AND which aids / subsidies show up in the vault panel.  Kept in sync with the
 * backend catalog's `countries` field. */
const COUNTRY_OPTIONS = [
  { code: "CA",   label: "🇨🇦 Canada" },
  { code: "US",   label: "🇺🇸 États-Unis" },
  { code: "FR",   label: "🇫🇷 France" },
  { code: "BE",   label: "🇧🇪 Belgique" },
  { code: "CH",   label: "🇨🇭 Suisse" },
  { code: "CM",   label: "🇨🇲 Cameroun" },
  { code: "SN",   label: "🇸🇳 Sénégal" },
  { code: "CI",   label: "🇨🇮 Côte d'Ivoire" },
  { code: "BJ",   label: "🇧🇯 Bénin" },
  { code: "MA",   label: "🇲🇦 Maroc" },
  { code: "TN",   label: "🇹🇳 Tunisie" },
  { code: "EU",   label: "🇪🇺 Europe (autre)" },
  { code: "INTL", label: "🌍 International / autre" },
];

function CountrySelect({ country, setCountry, testId }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">
        Pays du dossier
      </div>
      <select
        data-testid={testId}
        value={country}
        onChange={(e) => setCountry(e.target.value)}
        className="w-full rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
      >
        {COUNTRY_OPTIONS.map((c) => (
          <option key={c.code} value={c.code}>{c.label}</option>
        ))}
      </select>
      <div className="mt-1 text-[10px] font-mono text-[#8B8B8F]">
        Élargit la comparaison institutions au bon marché.
      </div>
    </label>
  );
}

function ContinueButton({ disabled, onClick, testId, label }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className="group inline-flex items-center gap-3 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:bg-[#8B8B8F] disabled:cursor-not-allowed text-white px-6 py-3.5 text-sm font-medium transition-all"
    >
      {label || "Ouvrir mon dossier"}
      <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
    </button>
  );
}
